import { faInfoCircle, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Divider, Flex, Text, Tooltip } from '@modusign/design-system';
import { forwardRef, Ref } from 'react';

import { useSelectionMap } from '@hooks';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import SearchInput from '@app/components/SearchInput';
import LabelListFilter from '@app/features/label/components/LabelPopovers/components/LabelListFilter';
import { LabelChangesContextProvider } from '@app/features/label/components/LabelPopovers/context/LabelChanges';
import {
  LabelChangeResult,
  LabelsLabelingState,
} from '@app/features/label/components/LabelPopovers/context/types';
import LabelCheckList from '@app/features/label/components/LabelPopovers/LabelApplyPopover/LabelCheckList/LabelCheckList';
import LabelInfiniteCheckList from '@app/features/label/components/LabelPopovers/LabelApplyPopover/LabelCheckList/LabelInfiniteCheckList';
import useAllWorkspaceLabelList from '@app/features/label/hooks/useAllWorkspaceLabelList';
import useLabelsFilters from '@app/features/label/hooks/useLabelsFilters';
import useResponsiveValue from '@app/hooks/useResponsiveValue';

import LabelApplyPopoverFooter from './LabelApplyPopoverFooter';

interface LabelApplyModalPopoverProps {
  excludedHidden?: boolean;
  showCloseButton?: 'mobile' | 'always';
  initialLabeled: LabelsLabelingState;
  mode?: 'infinite' | 'limit';
  onSubmit: (labelChangeResult: LabelChangeResult) => void | Promise<unknown>;
  onClose?: () => void;
}

const LabelApplyModalPopover = forwardRef(
  (
    {
      excludedHidden = false,
      showCloseButton = 'mobile',
      initialLabeled,
      mode = 'infinite',
      onSubmit,
      onClose,
      ...props
    }: LabelApplyModalPopoverProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const selected = useSelectionMap(initialLabeled);
    const [filters, { set, remove }] = useLabelsFilters();
    const { data: labels, isLoading } = useAllWorkspaceLabelList({
      excludedHidden,
      ...filters,
    });
    const isMobileWidth = useResponsiveValue([true, false, false]);

    return (
      <Box
        as="section"
        bg="white"
        border="1px solid"
        borderColor="boxBorderNeutralBase"
        borderRadius="4px"
        boxShadow="0px 3px 9px rgba(0, 0, 0, 0.1)"
        ref={ref}
        width="xxxs"
        {...props}
      >
        <LabelChangesContextProvider>
          <LabelApplyPopoverHeader
            onClose={showCloseButton === 'always' || isMobileWidth ? onClose : undefined}
          />
          <Flex as="section" flexDirection="column" width="100%">
            <Flex as="section" flexDirection="column" gap="14px" p={4}>
              <SearchInput
                placeholder="이름으로 찾기"
                onChange={(event) => set('name', event.target.value)}
                onClear={() => remove('name')}
              />
              <LabelListFilter
                filters={filters}
                onBookmarkFirst={(bookmarkFirst) => set('bookmarkFirst', bookmarkFirst)}
                onSortByChange={(sortBy) => {
                  set('sortBy', sortBy);
                  set('order', sortBy === 'createdAt' ? 'DESC' : 'ASC');
                }}
              />
            </Flex>
            <Divider color="light" />
            {isLoading && (
              <Box height="300px" position="relative" width="100%">
                <CenterLoadingSpinner />
              </Box>
            )}
            {!isLoading &&
              labels &&
              (mode === 'infinite' ? (
                <LabelInfiniteCheckList
                  filters={filters}
                  initialLabeled={initialLabeled}
                  labels={labels}
                />
              ) : (
                <LabelCheckList
                  filters={filters}
                  initialLabeled={initialLabeled}
                  labels={labels}
                  selected={selected}
                />
              ))}
            <Divider color="light" />
          </Flex>
          <LabelApplyPopoverFooter
            initialLabeled={initialLabeled}
            mode={mode}
            selected={selected}
            onSubmit={onSubmit}
          />
        </LabelChangesContextProvider>
      </Box>
    );
  }
);

function LabelApplyPopoverHeader({ onClose }: { onClose?: () => void }) {
  return (
    <Flex alignItems="center" as="header" justifyContent="space-between" pt={4} px={4} width="100%">
      <Text color="textNeutralLight" fontSize="xs">
        라벨
      </Text>
      {onClose ? (
        <Box as="button" onClick={onClose}>
          <Box as={FontAwesomeIcon} color="textNeutralLight" icon={faXmark} />
        </Box>
      ) : (
        <Tooltip content="도움말">
          <Box
            as={FontAwesomeIcon}
            color="textNeutralLight"
            cursor="pointer"
            icon={faInfoCircle}
            onClick={() => window.open('https://support.modusign.co.kr/119485644054')}
          />
        </Tooltip>
      )}
    </Flex>
  );
}

export default LabelApplyModalPopover;
